import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import PriceChart from './PriceChart';
import TradingViewChart from './TradingViewChart';
import { GET_STOCK_CHART } from '../graphql/queries/stocks';
import { usePortfolioContext } from '../contexts/PortfolioContext';

const CHART_TYPES = {
  CUSTOM: 'custom',
  TRADINGVIEW: 'tradingview',
};

const TRADINGVIEW_EXCHANGES = {
  AMS: 'EURONEXT',
  NASDAQ: 'NASDAQ',
  NMS: 'NASDAQ',
  NYQ: 'NYSE',
  NYSE: 'NYSE',
  GER: 'XETR',
  XETRA: 'XETR',
  LSE: 'LSE',
};

const tradingViewSymbol = (stock) => {
  const symbol = stock?.symbol || '';
  const exchange = TRADINGVIEW_EXCHANGES[(stock?.exchange || '').toUpperCase()];
  if (!exchange) return symbol;
  return `${exchange}:${symbol.split('.')[0]}`;
};

const StockPriceChart = ({ stock, position, plannedLevels = position?.exitStrategy?.levels || [] }) => {
  const { currentPortfolioId } = usePortfolioContext();
  const isImported = Boolean(stock?.stockId);
  const [chartType, setChartType] = useState(
    isImported ? CHART_TYPES.CUSTOM : CHART_TYPES.TRADINGVIEW,
  );
  const { data, loading, error } = useQuery(GET_STOCK_CHART, {
    variables: {
      symbol: stock?.symbol,
      stockId: stock?.stockId,
      portfolioId: currentPortfolioId,
    },
    skip: !isImported || !currentPortfolioId,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    setChartType(isImported ? CHART_TYPES.CUSTOM : CHART_TYPES.TRADINGVIEW);
  }, [isImported, stock?.stockId]);

  const chart = data?.stockChart;
  const visibleTransactions = useMemo(
    () => (chart?.transactions || []).filter((transaction) => !transaction.hidden),
    [chart?.transactions],
  );

  if (!isImported) {
    return (
      <TradingViewChart
        symbol={tradingViewSymbol(stock)}
        theme="dark"
        height="400px"
        width="100%"
      />
    );
  }

  const customChart = (() => {
    if (loading && !chart) {
      return (
        <div className="h-96 flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500" />
        </div>
      );
    }
    if (error) {
      return (
        <div className="h-96 flex items-center justify-center text-sm text-red-600 dark:text-red-400">
          Unable to load stock history.
        </div>
      );
    }
    if (!chart?.priceHistory?.length) {
      return (
        <div className="h-96 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
          No stock price history available.
        </div>
      );
    }
    // History, markers and averages from stockChart are server-converted to
    // portfolio currency. Never mix the native stock.quote.price here.
    return (
      <PriceChart
        data={chart.priceHistory}
        averageBuyPrice={chart.averageBuyPrice}
        averageSellPrice={chart.averageSellPrice}
        transactions={visibleTransactions}
        holdingsAmount={chart.holdingsAmount}
        ticker={stock.symbol}
        plannedLevels={plannedLevels.filter(
          (level) => (level.status || 'PLANNED').toUpperCase() !== 'FILLED',
        )}
      />
    );
  })();

  return (
    <div className="w-full">
      {chartType === CHART_TYPES.CUSTOM ? customChart : (
        <TradingViewChart
          symbol={tradingViewSymbol(stock)}
          theme="dark"
          height="400px"
          width="100%"
        />
      )}

      <div className="mt-3 flex justify-center">
        <div className="inline-flex rounded overflow-hidden border border-gray-300 dark:border-gray-600">
          <button
            type="button"
            onClick={() => setChartType(CHART_TYPES.CUSTOM)}
            className={`px-3 py-1.5 text-xs font-medium ${
              chartType === CHART_TYPES.CUSTOM
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            Custom
          </button>
          <button
            type="button"
            onClick={() => setChartType(CHART_TYPES.TRADINGVIEW)}
            className={`px-3 py-1.5 text-xs font-medium border-l border-gray-300 dark:border-gray-600 ${
              chartType === CHART_TYPES.TRADINGVIEW
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            TradingView
          </button>
        </div>
      </div>
    </div>
  );
};

export default StockPriceChart;
